import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Typography, 
  CircularProgress, 
  Paper, 
  Grid, 
  Chip,
  Box,
  Divider,
  Select,
  MenuItem,
  Button,
  Menu,
  MenuItem as MenuItemType
} from '@mui/material';
import { Download } from '@mui/icons-material';
import axiosInstance from '../utils/axiosConfig';
import { exportApplicationToPDF, exportApplicationToCSV } from '../utils/applicationExport';

interface FormField {
  id: string;
  type: string;
  name: string;
  label: string;
  helperText?: string;
}

interface ApplicationData {
  _id: string;
  formType: string;
  formTitle: string;
  formSchema: {
    fields: FormField[];
  };
  values: Record<string, any>;
  rawValues: Record<string, any>;
  status: string;
  submittedAt: string;
  submittedBy: string | null;
}

const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [application, setApplication] = useState<ApplicationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        const response = await axiosInstance.get<ApplicationData>(`/api/applications/submissions/${id}`);
        setApplication(response.data);
      } catch (err: any) {
        setError(err.message || 'An error occurred while fetching the application.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplication();
  }, [id]);

  const handleStatusChange = async (newStatus: string) => {
    try {
      await axiosInstance.patch(`/api/applications/submissions/${id}/status`, {
        status: newStatus
      });
      if (application) {
        setApplication({ ...application, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportPDF = () => {
    if (application) {
      exportApplicationToPDF(application);
    }
    handleExportMenuClose();
  };

  const handleExportCSV = () => {
    if (application) {
      exportApplicationToCSV(application);
    }
    handleExportMenuClose();
  };

  const renderFieldValue = (field: FormField, value: any) => {
    if (!value) return <Typography color="textSecondary">Not provided</Typography>;

    // Handle arrays (multi-select, checkboxes)
    if (Array.isArray(value)) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {value.map((item, index) => (
            <Chip key={index} label={item} size="small" />
          ))}
        </Box>
      );
    }

    // Handle URLs
    if (field.type === 'url' && typeof value === 'string') {
      return (
        <Typography>
          <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
            {value}
          </a>
        </Typography>
      );
    }

    // Handle long text
    if (field.type === 'textarea' && typeof value === 'string') {
      return (
        <Typography 
          sx={{ 
            whiteSpace: 'pre-line',
            backgroundColor: '#f5f5f5',
            padding: 2,
            borderRadius: 1,
            mt: 1
          }}
        >
          {value}
        </Typography>
      );
    }

    // Default text display
    return <Typography>{String(value)}</Typography>;
  };

  const stripHtmlTags = (html: string) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!application) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No application data found.</Typography>
      </Box>
    );
  }

  // Get applicant name from values
  const applicantName = application.values.firstName && application.values.lastName
    ? `${application.values.firstName} ${application.values.lastName}`
    : 'Applicant';

  return (
    <Paper elevation={3} sx={{ padding: 3, marginTop: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {application.formTitle}
            </Typography>
            <Typography variant="h5" color="primary" gutterBottom>
              {applicantName}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportMenuOpen}
          >
            Export
          </Button>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleExportMenuClose}
        >
          <MenuItem onClick={handleExportPDF}>Export as PDF</MenuItem>
          <MenuItem onClick={handleExportCSV}>Export as CSV</MenuItem>
        </Menu>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Submitted: {new Date(application.submittedAt).toLocaleString()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Status:</Typography>
            <Select
              value={application.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Dynamic Fields Section */}
      <Grid container spacing={3}>
        {application.formSchema.fields.map((field) => {
          const value = application.values[field.name];
          
          // Skip fields with no value
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return null;
          }

          return (
            <Grid item xs={12} key={field.id}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {stripHtmlTags(field.label)}
                </Typography>
                {field.helperText && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                    {stripHtmlTags(field.helperText)}
                  </Typography>
                )}
                {renderFieldValue(field, value)}
              </Box>
              <Divider />
            </Grid>
          );
        })}
      </Grid>

      {/* Actions Section */}
      <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={() => window.history.back()}>
          Back to List
        </Button>
        <Button variant="contained" color="primary">
          Email Applicant
        </Button>
      </Box>
    </Paper>
  );
};

export default ApplicationDetail;